import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Block {
  top: number;
  bottom: number;
}

const findCrossingBlock = (blocks: Block[], top: number, bottom: number): Block | undefined =>
  blocks.find(block => block.top > top && block.top < bottom && block.bottom > bottom);

// Keep fields and table rows together when they fit on one page.
export const getPageSlices = (height: number, pageHeight: number, blocks: Block[]): Block[] => {
  if (height <= 0 || pageHeight < 1) throw new Error('Dimensões inválidas para exportação.');
  const pages: Block[] = [];
  let top = 0;
  while (top < height) {
    let bottom = Math.min(top + Math.floor(pageHeight), height);
    if (bottom < height) {
      let crossing = findCrossingBlock(blocks, top, bottom);
      while (crossing) {
        bottom = Math.floor(crossing.top);
        crossing = findCrossingBlock(blocks, top, bottom);
      }
    }
    // An oversized block must be split, but must never prevent forward progress.
    if (bottom <= top) bottom = Math.min(top + Math.floor(pageHeight), height);
    pages.push({ top, bottom });
    top = bottom;
  }
  return pages;
};

export const exportChargebackPdf = async (report: HTMLElement): Promise<void> => {
  const blocks: Block[] = [];
  const scale = 2;
  const canvas = await html2canvas(report, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: 1200,
    onclone: clonedDocument => {
      const clonedReport = clonedDocument.getElementById(report.id);
      if (!clonedReport) throw new Error('Relatório indisponível para exportação.');
      clonedReport.style.width = '1000px';
      const reportTop = clonedReport.getBoundingClientRect().top;
      clonedReport.querySelectorAll('.chargeback-field, [role="row"], h2').forEach(element => {
        const rect = element.getBoundingClientRect();
        blocks.push({
          top: (rect.top - reportTop) * scale,
          bottom: (rect.bottom - reportTop) * scale,
        });
      });
    },
  });
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  const margin = 10;
  const width = pdf.internal.pageSize.getWidth() - margin * 2;
  const height = pdf.internal.pageSize.getHeight() - margin * 2;
  const pixelsPerMm = canvas.width / width;
  const pages = getPageSlices(canvas.height, height * pixelsPerMm, blocks);
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = canvas.width;
  const context = pageCanvas.getContext('2d');
  if (!context) throw new Error('Não foi possível preparar as páginas do relatório.');
  pages.forEach((page, index) => {
    pageCanvas.height = page.bottom - page.top;
    context.drawImage(
      canvas,
      0,
      page.top,
      canvas.width,
      pageCanvas.height,
      0,
      0,
      canvas.width,
      pageCanvas.height,
    );
    if (index > 0) pdf.addPage();
    pdf.addImage(
      pageCanvas.toDataURL('image/png'),
      'PNG',
      margin,
      margin,
      width,
      pageCanvas.height / pixelsPerMm,
    );
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    pdf.text(
      `${index + 1} / ${pages.length}`,
      pdf.internal.pageSize.getWidth() - margin,
      pdf.internal.pageSize.getHeight() - 4,
      { align: 'right' },
    );
  });
  pdf.save('relatorio-chargeback.pdf');
};
